import { Icon } from '@advanza/advanza_generic'
import PopUpNotification, {
    DELIVERY_TYPE_LABELS,
    DeliveryType,
    deliveryTypeIncludesPush,
    DeliveryTypes,
    POP_UP_NOTIFICATION,
} from '@advanza/interfaces/PopUpNotification'
import { Button } from '@advanza/shadcn-ui/src/components/ui/button'
import { ColumnDef, FilterFn } from '@tanstack/react-table'
import { DateIntervalSpan } from 'components/popUpNotifications/PopUpNotificationsList'
// optional icon
import { FIELDS, usePopUpNotification } from 'hooks/popUpnotificationsHooks'
import { Check } from 'lucide-react'
import { formatSendAtForCountry } from 'misc/notificationSendAt'
import { useState } from 'react'

export const ARCHIVED_COLUMN_ID = 'archived'
export const FOLDER_COLUMN_ID = 'folder'
export const NO_FOLDER_FILTER_VALUE = 0

/**
 * The auto-resolved filter function treats numeric columns as ranges and string columns as
 * substring matches, neither of which suits the exact-match dropdown filters.
 */
const exactMatchFilterFn: FilterFn<PopUpNotification> = (row, columnId, filterValue) =>
    row.getValue(columnId) === filterValue

const ArchiveCell = ({ notification }: { notification: PopUpNotification }) => {
    const { archivePopUpNotification } = usePopUpNotification()
    const [isBusy, setIsBusy] = useState(false)

    if (!notification.pop_up_notification_id) {
        return null
    }

    const onClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setIsBusy(true)
        try {
            await archivePopUpNotification(
                notification.pop_up_notification_id as number,
                !notification.archived,
            )
            window.location.reload()
        } catch (error) {
            console.error('Failed to archive notification', error)
            setIsBusy(false)
        }
    }

    return (
        <Button variant="ghost" size="sm" disabled={isBusy} onClick={onClick}>
            <span className="text-muted-foreground text-sm">
                {notification.archived ? 'Restore' : 'Archive'}
            </span>
        </Button>
    )
}

export const PopUpNotificationColumnsDef: ColumnDef<PopUpNotification>[] = [
    {
        accessorKey: 'pop_up_notification_id',
        header: () => <div className="font-bold ">#Id</div>,
        cell: ({ row }) => {
            const id = row.getValue('pop_up_notification_id') as string
            const [iconCopy, setIconCopy] = useState(false)
            // Don't show anything for new notifications
            if (!id) {
                return null
            }

            const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                navigator.clipboard && (await navigator.clipboard.writeText(id))
                setIconCopy(true)
                setTimeout(() => {
                    setIconCopy(false)
                }, 2000)
            }

            return (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="flex items-center gap-2 px-2 ">
                    {iconCopy ? (
                        <Check className="text-green-500 w-4 h-4" />
                    ) : (
                        <>
                            <span className="text-muted-foreground truncate max-w-[120px]">
                                #{id}
                            </span>
                        </>
                    )}
                </Button>
            )
        },
    },
    {
        accessorKey: FIELDS.TITLE,
        header: () => <div className="font-bold">Title</div>,
        cell: ({ row }) => {
            return <div className="font-bold c-grey">{row.getValue(FIELDS.TITLE)}</div>
        },
    },
    {
        accessorKey: FIELDS.LABEL,
        header: () => <div className="font-bold">Label</div>,
        cell: ({ row }) => {
            return <div className="c-grey">{row.getValue(FIELDS.LABEL)}</div>
        },
    },
    {
        id: FIELDS.AVAILABILITY,
        accessorFn: (row) => row.availability,
        accessorKey: FIELDS.AVAILABILITY,
        header: () => <div className="text-center font-bold">Active Period</div>,
        cell: ({ row }) => {
            const col = row.getValue(FIELDS.AVAILABILITY) as { from: string; to: string }

            return (
                <div className="text-center">
                    <small className="c-grey">
                        {row.getValue(FIELDS.AVAILABILITY) ? (
                            <DateIntervalSpan from={col.from} to={col.to} />
                        ) : (
                            'Always show'
                        )}
                    </small>
                </div>
            )
        },
        sortingFn: (rowA, rowB, columnId) => {
            const a = rowA.getValue(columnId) as { from: string }
            const b = rowB.getValue(columnId) as { from: string }

            return new Date(a.from).getTime() - new Date(b.from).getTime()
        },
    },
    {
        accessorKey: FIELDS.DELIVERY_TYPE,
        filterFn: exactMatchFilterFn,
        header: () => <div className="text-center font-bold">Delivery</div>,
        // The send time lives here rather than in a column of its own: it only applies to the push
        // delivery types, and the overview is already wide enough to scroll sideways.
        cell: ({ row }) => {
            const { push_send_at, push_sent_at, country_code } = row.original
            const deliveryType =
                (row.getValue(FIELDS.DELIVERY_TYPE) as DeliveryType) ?? DeliveryTypes.POP_UP

            return (
                <div className="text-center c-grey">
                    <small>{DELIVERY_TYPE_LABELS[deliveryType]}</small>
                    {deliveryTypeIncludesPush(deliveryType) && (
                        <small className="block" title="Scheduled send time">
                            {formatSendAtForCountry(push_send_at, country_code) || 'Not scheduled'}
                        </small>
                    )}
                    {push_sent_at && (
                        <small className="block text-green-700" title="Push already sent">
                            sent
                        </small>
                    )}
                </div>
            )
        },
    },
    {
        id: 'log_count',
        accessorKey: 'log_count',
        header: () => <div className="text-center font-bold">Log Count</div>,
        cell: ({ row }) => {
            const openCount = row.original.log_count_open || 0
            const clickCount = row.original.log_count_click || 0
            const pushSent = row.original.push_count_sent || 0
            const pushClicked = row.original.push_count_click || 0

            return (
                <div className="text-center c-grey">
                    <div>
                        <span title="pop-up opened">{openCount}</span>
                        <span title="pop-up clicked">({clickCount})</span>
                    </div>
                    {deliveryTypeIncludesPush(row.original.delivery_type) && (
                        <small className="block" title="push sent (clicked)">
                            push {pushSent}({pushClicked})
                        </small>
                    )}
                </div>
            )
        },
    },
    {
        accessorKey: FIELDS.USER_TARGET,
        header: () => <div className="text-center font-bold">Users</div>,
        cell: ({ row }) => {
            return (
                <div className="text-center c-lighter font-bold">
                    {row.getValue(FIELDS.USER_TARGET)}
                </div>
            )
        },
    },
    {
        accessorKey: FIELDS.SERVICES_TARGET,
        header: () => <div className="text-center font-bold">Services</div>,
        cell: ({ row }) => {
            const services = row.getValue(FIELDS.SERVICES_TARGET) as string[] | undefined
            const users = row.getValue(FIELDS.USER_TARGET)

            return (
                <div className="text-center c-grey ">
                    {users === POP_UP_NOTIFICATION.TARGETS.USER.SPECIFIC
                        ? '-'
                        : services
                          ? services.length
                          : POP_UP_NOTIFICATION.TARGETS.SERVICES.ALL}
                </div>
            )
        },
    },
    {
        accessorKey: FIELDS.STATUS,
        filterFn: exactMatchFilterFn,
        header: () => <div className="text-center font-bold">Status</div>,
        cell: ({ row }) => {
            const status = row.getValue(FIELDS.STATUS) as 0 | 1 | 2
            const getStatusLabel = () => {
                if (status === 1) return POP_UP_NOTIFICATION.STATUS.ACTIVE
                if (status === 2) return POP_UP_NOTIFICATION.STATUS.TRIGGERABLE
                return POP_UP_NOTIFICATION.STATUS.INACTIVE
            }
            const getStatusStyle = () => {
                if (status === 1) {
                    return {
                        backgroundColor: '#dcfce7', // green-100
                        color: '#166534', // green-800
                        borderColor: '#86efac', // green-300
                    }
                }
                if (status === 2) {
                    return {
                        backgroundColor: '#ffedd5', // orange-100
                        color: '#9a3412', // orange-800
                        borderColor: '#fdba74', // orange-300
                    }
                }
                return {
                    backgroundColor: '#f3f4f6', // gray-100
                    color: '#1f2937', // gray-800
                    borderColor: '#d1d5db', // gray-300
                }
            }
            return (
                <div className="text-center">
                    <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium border"
                        style={getStatusStyle()}>
                        {getStatusLabel()}
                    </span>
                </div>
            )
        },
    },
    {
        id: FOLDER_COLUMN_ID,
        accessorFn: (row) => row.pop_up_notification_folder_id ?? NO_FOLDER_FILTER_VALUE,
        filterFn: exactMatchFilterFn,
        header: () => <div className="text-center font-bold">Folder</div>,
        cell: ({ row }) => (
            <div className="text-center c-grey">
                <small>{row.original.folder_name ?? '-'}</small>
            </div>
        ),
    },
    {
        // Also the column the Archived status filter reads, so archived rows can be hidden by default.
        id: ARCHIVED_COLUMN_ID,
        accessorFn: (row) => Boolean(row.archived),
        filterFn: exactMatchFilterFn,
        header: '',
        cell: ({ row }) => <ArchiveCell notification={row.original} />,
    },
    {
        id: 'expand',
        accessorKey: '',
        header: '',
        cell: () => {
            return <Icon>{'arrow_drop_down'}</Icon>
        },
    },
]
